import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, PenLine, UserX, UserCheck } from 'lucide-react';
import Layout from '../../components/Layout';
import PermissionTree from '../../components/PermissionTree';
import { userService, UserDTO } from '../../services/userService';
import { Permission } from '../../types';
import { useModule } from '../../context/ModuleContext';
import { filterPermissionsByModule } from '../../utils/permissionModuleVisibility';
import '../../components/PermissionTree.css';
import '../../pages/Products/ProductCategoryDetailPage.css';
import '../../pages/Products/ProductCategoryCreatePage.css';

const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { moduleCode } = useModule();
  const [user, setUser] = useState<UserDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message || null
  );

  useEffect(() => {
    loadUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadUser = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.getUserById(id || '0');
      if (response?.data?.data) {
        setUser(response.data.data);
      }
      setLoading(false);
    } catch {
      setError(t('user.failedToLoad'));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="create-container">
          <div className="alert alert-danger">{error || t('user.userNotFound')}</div>
          <button className="ims-back-btn" onClick={() => navigate('/users')}>
            <ArrowLeft size={16} strokeWidth={2} />
          </button>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  return (
    <Layout>
      <div className="create-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/users')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{user.firstName} {user.lastName}</div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate(`/users/${user.id}/edit`)}
            >
              <PenLine size={15} strokeWidth={2} />
              {t('user.editUser')}
            </button>
            {user.isActive ? (
              <button
                type="button"
                className="ims-btn ims-btn-danger"
                onClick={() => navigate(`/users/${user.id}/deactivate`)}
              >
                <UserX size={15} strokeWidth={2} />
                {t('user.deactivateUser')}
              </button>
            ) : (
              <button
                type="button"
                className="ims-btn ims-btn-primary"
                onClick={() => navigate(`/users/${user.id}/deactivate`)}
              >
                <UserCheck size={15} strokeWidth={2} />
                {t('user.activateUser')}
              </button>
            )}
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            <strong>{t('common.success')}:</strong> {successMessage}
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="detail-content">
          <div className="detail-main-section">

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('user.accountInfo')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('user.email')}</span>
                    <span className="detail-value"><code>{user.email}</code></span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('user.fullName')}</span>
                    <span className="detail-value">{user.firstName} {user.lastName}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('user.status')}</span>
                    <span className="detail-value">
                      <span className={`badge ${user.isActive ? 'badge-active' : 'badge-inactive'}`}>
                        {user.isActive ? t('user.active') : t('user.inactive')}
                      </span>
                    </span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('user.lastLogin')}</span>
                    <span className="detail-value">
                      {user.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString(langCode)
                        : t('user.neverLoggedIn')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('user.rolesAndPermissions')}</h2>
              </div>
              <div className="detail-card-body">
                {user.roles && user.roles.length > 0 ? (
                  <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                      {user.roles.map((role) => (
                        <span
                          key={role.id}
                          className="badge badge-draft"
                          style={{ fontSize: '13px', padding: '6px 12px' }}
                          title={role.description}
                        >
                          {role.name}
                        </span>
                      ))}
                    </div>
                    {(() => {
                      const seen = new Set<number>();
                      const allPerms: Permission[] = [];
                      user.roles.forEach((role) => {
                        role.permissions?.forEach((p) => {
                          if (!seen.has(p.id)) {
                            seen.add(p.id);
                            allPerms.push(p as Permission);
                          }
                        });
                      });
                      const visiblePerms = filterPermissionsByModule(allPerms, moduleCode);
                      if (visiblePerms.length === 0) return null;
                      return (
                        <>
                          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                            {t('role.permissionsCount')}: <strong>{visiblePerms.length}</strong>
                          </p>
                          <PermissionTree
                            allPermissions={visiblePerms}
                            selectedIds={visiblePerms.map((p) => p.id)}
                            readOnly
                          />
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>{t('user.noRoles')}</p>
                )}
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('user.auditInformation')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('user.userId')}</span>
                    <span className="detail-value">{user.id}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UserDetailPage;
